import api from '../../utils/api';
import { OrderListItem, OrdersSummary } from './orders.types';

const STORAGE_KEY = 'mc_orders_data';

export class OrdersModel {
  /**
   * Saves updated order sequence to local storage.
   */
  public static saveOrders(orders: OrderListItem[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
  }

  /**
   * Envia um novo pedido para criação no banco de dados MySQL via API.
   */
  public static async saveOrderToServer(order: OrderListItem): Promise<boolean> {
    try {
      const payload = {
        number: order.number,
        customer_name: order.customerName,
        departure: order.departure,
        city: order.city,
        is_priority: order.isPriority,
        is_emergence: order.isEmergence,
        customStatus: order.customStatus,
        custom_status: order.customStatus,
        items: order.items.map(item => ({
          productId: item.productId,
          isProduced: item.isProduced,
          status: item.status || 'Aguardando',
          colorHex: item.colorHex || '#198754',
          collaboratorName: item.collaboratorName || null,
          setor: item.setor || null,
          periods: item.periods || []
        }))
      };

      const response = await api.post('/orders', payload);
      return response.status === 200 || response.status === 201;
    } catch (e) {
      console.error("Erro ao salvar pedido no servidor MySQL", e);
      return false;
    }
  }

  /**
   * Atualiza um pedido existente no banco de dados MySQL via API.
   */
  public static async updateOrderOnServer(order: OrderListItem): Promise<boolean> {
    try {
      const payload = {
        id: order.id,
        number: order.number,
        customer_name: order.customerName,
        departure: order.departure,
        city: order.city,
        is_priority: order.isPriority,
        is_emergence: order.isEmergence,
        statusName: order.statusName,
        customStatus: order.customStatus,
        custom_status: order.customStatus,
        items: order.items.map(item => ({
          id: item.id,
          productId: item.productId,
          isProduced: item.isProduced,
          status: item.status,
          colorHex: item.colorHex,
          collaboratorName: item.collaboratorName,
          setor: item.setor,
          periods: item.periods || []
        }))
      };

      const response = await api.post('/orders', payload);
      return response.status === 200 || response.status === 201;
    } catch (e) {
      console.error("Erro ao atualizar pedido no servidor MySQL", e);
      return false;
    }
  }

  /**
   * Remove um pedido do banco de dados MySQL via API.
   */
  public static async deleteOrderFromServer(id: number): Promise<boolean> {
    try {
      const response = await api.post('/orders/delete', { id });
      return response.status === 200 || response.status === 201;
    } catch (e) {
      console.error("Erro ao deletar pedido no servidor MySQL", e);
      return false;
    }
  }

  /**
   * Loads list of orders from database API, fallbacks to localStorage.
   */
  public static async fetchOrders(): Promise<OrderListItem[]> {
    try {
      const response = await api.get('/orders');
      if (response.data && Array.isArray(response.data)) {
        // Salva uma cópia local atualizada para cache/fallback
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data));
        return response.data as OrderListItem[];
      }
    } catch (e) {
      console.warn("API Offline, falling back to local state.", e);
    }

    // Check localStorage first
    const localData = localStorage.getItem(STORAGE_KEY);
    if (localData) {
      try {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (err) {
        console.error("Error parsing local orders data", err);
      }
    }

    const defaultOrders: OrderListItem[] = [];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultOrders));
    return defaultOrders;
  }

  public static calculateSummary(orders: OrderListItem[]): OrdersSummary {
    let totalProducts = 0;
    let inProduction = 0;

    orders.forEach(o => {
      totalProducts += o.items.length;
      if (o.statusName.toLowerCase().includes('produção') || o.statusName.toLowerCase().includes('wel')) {
        inProduction++;
      }
    });

    return {
      totalOrders: orders.length,
      totalProducts: totalProducts,
      inProduction: inProduction
    };
  }
}
